import { useEffect, useRef, useState, type ReactElement } from 'react'
import { RouteDetailsTestTags } from './testTags'
import { RouteDetailsStrings, type RouteDetailsStringSet } from './strings'
import type { MapCoordinate } from './sections'
import { loadMapKit } from './loadMapKit'
import { useMapKitToken } from './useMapKitToken'
import type { MapKit, MapKitCoordinate, MapKitCoordinateRegion, MapKitMap } from './mapkit'
import styles from './MapRow.module.css'
import { DestinationMapRow } from './DestinationMapRow'
import { resolveMapDark } from './resolveMapDark'

/**
 * Destination map row.
 *
 * Two shapes, mirroring iOS `DesignKit.MapRowView`:
 *
 *  - **Ride route:** only `destinationCoordinate` → a live Apple map
 *    pinned on the destination (`DestinationMapRow`, iOS `MapRowView`
 *    without an origin). No coordinate, or the test build → the static
 *    grid + centred blue pin (byte-stable baseline).
 *
 *  - **Walk (a pure-walk route, or one street walk of a ride route):**
 *    both `originCoordinate` and
 *    `destinationCoordinate` present → a real Apple MapKit JS map drawing
 *    a STREET-FOLLOWING walking route between the two endpoints
 *    (`mapkit.Directions`, `.Walking`), both endpoint markers, region fit
 *    to the route. The `map-view` a11y id flips to `map-view-walk-route`
 *    once the route is drawn (iOS `_isWalkRoute && routeCoordinates≥2`).
 *
 * **Determinism seam** (`VITE_E2E_HARNESS` build — the one Playwright's
 * preview server runs, present for BOTH visual + e2e, absent in
 * production): the walk map does NOT load MapKit JS or call Directions
 * (both async + network → non-deterministic). It renders a deterministic
 * STRAIGHT static line origin→destination instead, so snapshot/e2e
 * baselines stay stable. Only real production builds load MapKit +
 * Directions. A MapKit / Directions failure is fail-safe — no overlay,
 * markers only, never a throw into the host.
 */

/**
 * `true` under the `VITE_E2E_HARNESS` build (Playwright visual + e2e).
 * Inlined by Vite at build time; collapses to a literal `false` in
 * `build:production` / `build:nyc`, so real users always get the
 * live MapKit walk route. Same flag that gates the harness routes.
 */
const IS_TEST_BUILD = import.meta.env.VITE_E2E_HARNESS === '1'

/** `--mm-color-accent-blue` literal — MapKit JS cannot read CSS vars. */
const ACCENT_BLUE = '#0a84ff'

export interface MapRowProps {
  readonly stationName: string
  /** Both present → a direct walk (walking route between them); only the
   *  destination → a ride route's live map pinned on it; neither → the
   *  static single-pin placeholder. */
  readonly originCoordinate?: MapCoordinate | null
  readonly destinationCoordinate?: MapCoordinate | null
  /** Localised copy (aria label). Defaults to English; host threads the
   *  localised set — same seam as the other rows. */
  readonly strings?: RouteDetailsStringSet
}

export function MapRow({
  stationName,
  originCoordinate,
  destinationCoordinate,
  strings = RouteDetailsStrings,
}: MapRowProps): ReactElement {
  const isWalkRoute = originCoordinate != null && destinationCoordinate != null
  if (isWalkRoute) {
    return (
      <WalkRouteMapRow
        stationName={stationName}
        origin={originCoordinate}
        destination={destinationCoordinate}
        strings={strings}
      />
    )
  }
  if (destinationCoordinate != null && !IS_TEST_BUILD) {
    return (
      <DestinationMapRow
        stationName={stationName}
        destination={destinationCoordinate}
        strings={strings}
      />
    )
  }
  return <StaticMapRow stationName={stationName} strings={strings} />
}

/** Static grid + centred blue pin (unchanged ride-route behaviour). */
function StaticMapRow({
  stationName,
  strings,
}: {
  stationName: string
  strings: RouteDetailsStringSet
}): ReactElement {
  return (
    <div
      className={styles.map}
      data-testid={RouteDetailsTestTags.map}
      role="img"
      aria-label={strings.mapOf(stationName)}
    >
      <span className={styles.pin} data-testid="rd-map-pin">
        <span className={styles.pinDot} />
      </span>
    </div>
  )
}

interface WalkRouteMapRowProps {
  readonly stationName: string
  readonly origin: MapCoordinate
  readonly destination: MapCoordinate
  readonly strings: RouteDetailsStringSet
}

/**
 * Compile-time branch on the build-level determinism seam. Under the
 * harness build the live component (and its MapKit hooks / token fetch)
 * is never instantiated — only the deterministic placeholder — so the
 * conditional never breaks the rules-of-hooks (the branch is constant
 * for the whole build).
 */
function WalkRouteMapRow(props: WalkRouteMapRowProps): ReactElement {
  if (IS_TEST_BUILD) {
    return <WalkMapDeterministic {...props} />
  }
  return <WalkMapLive {...props} />
}

/**
 * Deterministic walk placeholder — a straight line origin→destination
 * between two dots over the static grid. Mirrors iOS `MapRowView`
 * snapshot placeholder (diagonal 0.25,0.7 → 0.75,0.3). The a11y id is
 * `map-view-walk-route` (the route IS drawn, deterministically).
 */
function WalkMapDeterministic({ stationName, strings }: WalkRouteMapRowProps): ReactElement {
  return (
    <div
      className={styles.map}
      data-testid={RouteDetailsTestTags.mapWalkRoute}
      role="img"
      aria-label={strings.mapOf(stationName)}
    >
      <WalkPlaceholder />
    </div>
  )
}

/**
 * Live walk map — real MapKit JS with a `mapkit.Directions` walking
 * route. A skeleton covers the row until Directions answers (owner
 * 2026-10-19: the straight-line placeholder flashed before the route and
 * claimed a path that does not exist); then the route, or — when
 * Directions finds none — the map with its A / B pins. The a11y id flips to
 * `map-view-walk-route` once the route is drawn.
 */
function WalkMapLive({
  stationName,
  origin,
  destination,
  strings,
}: WalkRouteMapRowProps): ReactElement {
  const mapDivRef = useRef<HTMLDivElement | null>(null)
  const tokenEntry = useMapKitToken()
  const [routeDrawn, setRouteDrawn] = useState(false)
  const [directionsAnswered, setDirectionsAnswered] = useState(false)

  useEffect(() => {
    if (!tokenEntry || !mapDivRef.current) {
      return
    }
    let cancelled = false
    let map: MapKitMap | null = null
    loadMapKit()
      .then((mapkit) => {
        if (cancelled || !mapDivRef.current) {
          return
        }
        if (!mapkit.initialized) {
          mapkit.init({
            authorizationCallback: (done) => {
              done(tokenEntry.token)
            },
          })
        }
        map = new mapkit.Map(mapDivRef.current, {
          isScrollEnabled: false,
          isZoomEnabled: false,
          isRotationEnabled: false,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          colorScheme: resolveMapDark() ? 'dark' : 'light',
        })
        const originCoord = new mapkit.Coordinate(origin.latitude, origin.longitude)
        const destCoord = new mapkit.Coordinate(destination.latitude, destination.longitude)
        // Fit both endpoints up front; refined to the route bounds once
        // Directions resolves.
        map.region = regionFitting(mapkit, [originCoord, destCoord])
        // Lettered like the route's own A / B bubbles: the walk starts at A.
        const originPin = new mapkit.MarkerAnnotation(originCoord, {
          color: ACCENT_BLUE,
          glyphText: 'A',
        })
        const destPin = new mapkit.MarkerAnnotation(destCoord, {
          color: ACCENT_BLUE,
          glyphText: 'B',
        })
        map.addAnnotation(originPin)
        map.addAnnotation(destPin)

        const directions = new mapkit.Directions()
        directions.route(
          {
            origin: originCoord,
            destination: destCoord,
            transportType: mapkit.Directions.Transport.Walking,
          },
          (error, data) => {
            // Fail-safe: a failed / empty Directions response leaves the
            // two markers with no polyline — never throws into the host.
            if (cancelled || map === null) {
              return
            }
            setDirectionsAnswered(true)
            if (error !== null || data.routes.length === 0) {
              return
            }
            const polyline = data.routes[0].polyline
            polyline.style = new mapkit.Style({ lineWidth: 4, strokeColor: ACCENT_BLUE })
            map.addOverlay(polyline)
            map.showItems([originPin, destPin, polyline], { animate: false })
            setRouteDrawn(true)
          },
        )
      })
      .catch(() => {
        // Stay on the placeholder when MapKit JS fails to load.
      })
    return (): void => {
      cancelled = true
      if (map) {
        map.destroy()
      }
      setRouteDrawn(false)
      setDirectionsAnswered(false)
    }
  }, [tokenEntry, origin.latitude, origin.longitude, destination.latitude, destination.longitude])

  return (
    <div
      className={styles.map}
      data-testid={routeDrawn ? RouteDetailsTestTags.mapWalkRoute : RouteDetailsTestTags.map}
      role="img"
      aria-label={strings.mapOf(stationName)}
    >
      <div
        ref={mapDivRef}
        className={styles.canvas}
        data-testid="rd-map-canvas"
        aria-hidden="true"
      />
      {!directionsAnswered && (
        <div className={styles.skeleton} data-testid="rd-map-skeleton" aria-hidden="true" />
      )}
    </div>
  )
}

/**
 * The deterministic straight-line overlay: a line between two dots at the
 * iOS-mirrored fractions (0.25,0.7 → 0.75,0.3). Percentage-positioned so
 * it never distorts and renders identically across runs.
 */
function WalkPlaceholder(): ReactElement {
  return (
    <div className={styles.walk} data-testid="rd-map-walk-line" aria-hidden="true">
      <svg className={styles.walkLine} preserveAspectRatio="none">
        <line x1="25%" y1="70%" x2="75%" y2="30%" />
      </svg>
      <span className={`${styles.walkDot} ${styles.walkDotStart}`} />
      <span className={`${styles.walkDot} ${styles.walkDotEnd}`} />
    </div>
  )
}

/** A `CoordinateRegion` fitting the given coordinates with a margin so
 *  the pins + route aren't clipped (mirrors iOS `_region(fitting:)`). */
function regionFitting(
  mapkit: MapKit,
  coordinates: readonly MapKitCoordinate[],
): MapKitCoordinateRegion {
  const lats = coordinates.map((c) => c.latitude)
  const lngs = coordinates.map((c) => c.longitude)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const center = new mapkit.Coordinate((minLat + maxLat) / 2, (minLng + maxLng) / 2)
  // 1.6× padding, with a floor so a very short walk still frames well.
  const span = new mapkit.CoordinateSpan(
    Math.max((maxLat - minLat) * 1.6, 0.004),
    Math.max((maxLng - minLng) * 1.6, 0.004),
  )
  return new mapkit.CoordinateRegion(center, span)
}
